import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { CountdownMode, CountdownProps } from './index';

// ============================================================================
// Types
// ============================================================================

type TimeRemaining = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
};

type CountdownCallback = () => void;

// ============================================================================
// Shared Countdown Update Loop (singleton pattern)
// ============================================================================

const countdownCallbacks = new Set<CountdownCallback>();
let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

function registerCountdownCallback(callback: CountdownCallback) {
  countdownCallbacks.add(callback);

  // Start the shared interval if this is the first callback
  if (countdownCallbacks.size === 1 && !countdownIntervalId) {
    countdownIntervalId = setInterval(() => {
      countdownCallbacks.forEach((cb) => cb());
    }, 1000);
  }

  // Return unregister function
  return () => {
    countdownCallbacks.delete(callback);
    // Stop the interval if no more callbacks
    if (countdownCallbacks.size === 0 && countdownIntervalId) {
      clearInterval(countdownIntervalId);
      countdownIntervalId = null;
    }
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate time remaining until target date
 */
function calculateTimeRemaining(
  targetDate: Date,
  showDays: boolean,
  showHours: boolean,
  showMinutes: boolean,
  showSeconds: boolean,
): TimeRemaining {
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  let totalSeconds = Math.floor(diffMs / 1000);
  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  // Calculate based on visible units with rollover
  if (showDays) {
    days = Math.floor(totalSeconds / 86400);
    totalSeconds %= 86400;
  }

  if (showHours) {
    hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
  } else if (showDays) {
    // Roll hours into days if hours are hidden but days are shown
    const extraDays = Math.floor(totalSeconds / 86400);
    days += extraDays;
    totalSeconds %= 86400;
  }

  if (showMinutes) {
    minutes = Math.floor(totalSeconds / 60);
    totalSeconds %= 60;
  } else if (showHours) {
    // Roll minutes into hours if minutes are hidden but hours are shown
    const extraHours = Math.floor(totalSeconds / 3600);
    hours += extraHours;
    totalSeconds %= 3600;
  } else if (showDays) {
    // Roll minutes into days if both hours and minutes are hidden
    const extraDays = Math.floor(totalSeconds / 86400);
    days += extraDays;
    totalSeconds %= 86400;
  }

  if (showSeconds) {
    seconds = totalSeconds;
  } else if (showMinutes) {
    // Roll seconds into minutes if seconds are hidden but minutes are shown
    const extraMinutes = Math.floor(totalSeconds / 60);
    minutes += extraMinutes;
  } else if (showHours) {
    // Roll seconds into hours
    const extraHours = Math.floor(totalSeconds / 3600);
    hours += extraHours;
  } else if (showDays) {
    // Roll seconds into days
    const extraDays = Math.floor(totalSeconds / 86400);
    days += extraDays;
  }

  return { days, hours, minutes, seconds, isExpired: false };
}

/**
 * Get the target date for daily mode (today or tomorrow based on current time)
 */
function getDailyModeTargetDate(timeString: string): Date {
  const now = new Date();
  const targetDate = new Date(timeString);

  // Create today's date with the target time
  const todayTarget = new Date(now);
  todayTarget.setHours(targetDate.getHours(), targetDate.getMinutes(), targetDate.getSeconds(), 0);

  // If today's target has passed, use tomorrow
  if (todayTarget.getTime() <= now.getTime()) {
    todayTarget.setDate(todayTarget.getDate() + 1);
  }

  return todayTarget;
}

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * Hook to manage countdown timer logic
 * Handles time calculations, daily mode reset, and shared update loop
 */
export function useCountdownTimer(
  dateTime: string,
  mode: CountdownMode,
  showDays: boolean,
  showHours: boolean,
  showMinutes: boolean,
  showSeconds: boolean,
  evergreenDuration?: number,
) {
  // Parse target date - memoize to prevent creating new Date object on every render
  const targetDate = useMemo(() => {
    if (!dateTime) return new Date();
    if (mode === 'daily') {
      return getDailyModeTargetDate(dateTime);
    }
    if (mode === 'evergreen' && evergreenDuration) {
      // For evergreen mode in editor/preview: start fresh countdown from current time
      return new Date(Date.now() + evergreenDuration * 1000);
    }
    return new Date(dateTime);
  }, [dateTime, mode, evergreenDuration]);

  // State for countdown values
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() => {
    return calculateTimeRemaining(targetDate, showDays, showHours, showMinutes, showSeconds);
  });

  // Track when countdown hits zero for daily mode reset
  const zeroDisplayStartTimeRef = useRef<number | null>(null);

  // Store the update function in a ref so it always has latest values
  const updateCountdownRef = useRef(() => {});

  // Update the ref whenever dependencies change
  updateCountdownRef.current = () => {
    let newTime = calculateTimeRemaining(targetDate, showDays, showHours, showMinutes, showSeconds);

    // Daily mode: Handle midnight reset with 1-second zero display
    if (mode === 'daily' && newTime.isExpired) {
      if (zeroDisplayStartTimeRef.current === null) {
        zeroDisplayStartTimeRef.current = Date.now();
      } else {
        const elapsedMs = Date.now() - zeroDisplayStartTimeRef.current;
        if (elapsedMs >= 1000) {
          const nextTarget = getDailyModeTargetDate(dateTime);
          newTime = calculateTimeRemaining(
            nextTarget,
            showDays,
            showHours,
            showMinutes,
            showSeconds,
          );
          zeroDisplayStartTimeRef.current = null;
        }
      }
    } else {
      zeroDisplayStartTimeRef.current = null;
    }

    setTimeRemaining(newTime);
  };

  // Register with shared countdown loop (only once)
  useEffect(() => {
    updateCountdownRef.current();
    const stableCallback = () => {
      updateCountdownRef.current();
    };
    const unregister = registerCountdownCallback(stableCallback);
    return () => {
      unregister();
    };
  }, []);

  // Recalculate immediately when key props change
  useEffect(() => {
    updateCountdownRef.current();
  }, [targetDate, showDays, showHours, showMinutes, showSeconds]);

  return timeRemaining;
}

/**
 * Hook to manage label editing logic
 * Handles double-click to edit, blur to save, and keyboard events
 */
export function useCountdownLabelEditor(
  isEditable: boolean,
  isLocked: boolean,
  setProp: (callback: (props: CountdownProps) => void) => void,
) {
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const labelRefs = useRef<Record<string, HTMLSpanElement | null>>({});

  const handleLabelDoubleClick = useCallback(
    (unit: string) => {
      if (isEditable && !isLocked) {
        setEditingLabel(unit);
        setTimeout(() => {
          const element = labelRefs.current[unit];
          if (element) {
            element.focus();
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(element);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
        }, 0);
      }
    },
    [isEditable, isLocked],
  );

  const handleLabelBlur = useCallback(
    (unit: 'days' | 'hours' | 'minutes' | 'seconds', e: React.FocusEvent<HTMLSpanElement>) => {
      let text = e.target.textContent || '';
      const chars = Array.from(text);
      if (chars.length > 20) {
        text = chars.slice(0, 20).join('');
      }

      setProp((props: CountdownProps) => {
        if (unit === 'days') {
          props.labelDays = text;
        } else if (unit === 'hours') {
          props.labelHours = text;
        } else if (unit === 'minutes') {
          props.labelMinutes = text;
        } else if (unit === 'seconds') {
          props.labelSeconds = text;
        }
      });
      setEditingLabel(null);
    },
    [setProp],
  );

  const handleLabelKeyDown = useCallback(
    (unit: 'days' | 'hours' | 'minutes' | 'seconds', e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        labelRefs.current[unit]?.blur();
      }

      const text = e.currentTarget.textContent || '';
      const charCount = Array.from(text).length;
      if (charCount >= 20 && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }
    },
    [],
  );

  return {
    editingLabel,
    labelRefs,
    handleLabelDoubleClick,
    handleLabelBlur,
    handleLabelKeyDown,
  };
}
